import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ListCard from "@/components/cards/ListCard";
import TemplateRecipientCard from "@/components/cards/TemplateRecipientCard";
import { TemplateDetailsSkeleton } from "@/components/skeletons";
import TransferOwnershipButton from "@/components/buttons/TransferOwnershipButton";
import TransferOwnershipModal from "@/components/modals/TransferOwnershipModal";
import { getTemplateById, transferTemplateOwnership } from "@/services/template.service";
import { NO_DOCUMENTS_FOUND, NO_RECIPIENT_FOUND } from "@/utils/constants/messages";
import { GLOBAL_ORGANIZATION, LABELS } from "@/utils/constants/appConstants/commonConstants";
import { DateFormatter, TimeFormatter } from "@/utils/functions/helperFunctions";
import { isFeaturePermitted } from "@/utils/functions/rbac";
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";

const TemplateDetails = () => {
    const [isTransferTemplateOwnershipPermitted, setIsTransferTemplateOwnershipPermitted] = useState(false);
    const [templateDetails, setTemplateDetails] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [transferLoading, setTransferLoading] = useState(false);
    const { id } = useParams();
    const currentOrganization = getCurrentOrganization();

    useEffect(() => {
        if (!id) {
            return;
        }

        setIsTransferTemplateOwnershipPermitted(isFeaturePermitted("AdminPortalTransferTemplateOwnership"));
        setLoading(true);
        getTemplateById(
            id,
            (res: any) => {
                setTemplateDetails(res);
                setLoading(false);
            },
            (err: any) => {
                console.log(err);
                setLoading(false);
            }
        );
    }, [id]);

    const handleTransferOwnership = async (selectedUser: any) => {
        if (!selectedUser || !id) {
            return;
        }

        setTransferLoading(true);
        try {
            const payload: { user_id?: string; email_address?: string } = {};
            if (selectedUser.user?.id) {
                payload.user_id = selectedUser.user.id;
            } else if (selectedUser.user?.email_address) {
                payload.email_address = selectedUser.user.email_address;
            } else {
                throw new Error("No valid user identifier found");
            }
            await transferTemplateOwnership(id, payload);
            setShowTransferModal(false);
        } catch (error) {
            console.error("Error transferring template ownership:", error);
        } finally {
            setTransferLoading(false);
        }
    };

    if (loading) {
        return <TemplateDetailsSkeleton />;
    }

    const recipients = templateDetails?.recipients
        ? [...templateDetails.recipients].sort(
            (a: { priority: number }, b: { priority: number }) => a.priority - b.priority
        )
        : [];

    return (
        <>
            <div className="gs-detail-header">
                <h3 className="gs-detail-title" id="template-detail-subject">
                    {templateDetails?.name}
                </h3>
                {isTransferTemplateOwnershipPermitted && currentOrganization?.name !== GLOBAL_ORGANIZATION ? (
                    <TransferOwnershipButton
                        id="template-detail-transfer-ownership-btn"
                        iconId="template-detail-transfer-ownership-icon"
                        onClick={() => setShowTransferModal(true)}
                    />
                ) : null}
            </div>

            <div className="gs-list-page-card mb-4">
                <ul className="m-0 list-none p-0">
                    <ListCard id="template-detail-id" label="Template ID" value={templateDetails?.id} />
                    <ListCard
                        id="template-detail-created-at"
                        label="Created at"
                        value={`${DateFormatter(templateDetails?.created_at)} | ${TimeFormatter(templateDetails?.created_at)}`}
                    />
                    <ListCard
                        id="template-detail-changed-at"
                        label="Last changed at"
                        value={`${DateFormatter(templateDetails?.updated_at)} | ${TimeFormatter(templateDetails?.updated_at)}`}
                    />
                    <ListCard
                        id="template-detail-expired-at"
                        label="Expires After"
                        value={templateDetails?.expiry_after}
                        valueClass="text-danger"
                    />
                </ul>
            </div>

            <h5 className="gs-detail-section-title" id="template-detail-recipients-title">
                Recipients
            </h5>
            <div className="gs-list-page-card mb-4">
                <TemplateRecipientCard
                    recipients={recipients}
                    emptyMessage={NO_RECIPIENT_FOUND}
                    emptyClassName="gs-detail-empty"
                />
            </div>

            <h5 className="gs-detail-section-title" id="template-detail-documents-title">
                Documents
            </h5>
            <div className="gs-list-page-card mb-4">
                <ul className="m-0 list-none p-0">
                    {templateDetails?.documents?.length > 0 ? (
                        templateDetails.documents.map((document: any, key: number) => (
                            <li className="gs-envelope-detail-document-item" key={document.id ?? key}>
                                <div className="gs-envelope-detail-document-icon">
                                    <em className="icon ni ni-file-pdf" id={`template-detail-document-icon-${key}`} />
                                </div>
                                <div
                                    className="gs-envelope-detail-document-title"
                                    id={`template-detail-document-title-${key}`}
                                >
                                    {document.title}
                                </div>
                            </li>
                        ))
                    ) : (
                        <li className="gs-detail-empty" id="template-detail-no-doc-found-message">
                            {NO_DOCUMENTS_FOUND}
                        </li>
                    )}
                </ul>
            </div>

            <TransferOwnershipModal
                show={showTransferModal}
                onHide={() => setShowTransferModal(false)}
                onTransferOwnership={handleTransferOwnership}
                loading={transferLoading}
                currentOwnerId={templateDetails?.owner?.id}
                title={LABELS.TRANSFER_TEMPLATE_OWNERSHIP}
                description={LABELS.TRANSFER_OWNERSHIP_DESCRIPTION}
            />
        </>
    );
};

export default TemplateDetails;
